
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import SessionsModule from '@/store/modules/Sessions';
import UserModule from '@/store/modules/User';
import PhotosModule from '@/store/modules/Photos';
import Folder from '@/model/Folder';
import ConfirmDelete from '@/components/session/ConfirmDelete.vue';
import ConfirmationModel from '@/components/ConfirmationModel';
import { polishPlurals } from 'polish-plurals';
import { createInterfaceSound, playInterfaceSound } from '@/service/interfaceSound';

const sessionsModule = getModule(SessionsModule, store);
const userModule = getModule(UserModule, store);
const photosModule = getModule(PhotosModule, store);

@Component({
    components: {
        ConfirmDelete,
    },
})
export default class FolderCard extends Vue {
    @Prop({ type: Object, required: true })
    public folder!: Folder;

    public menu: boolean = false;
    public deleteConfirmationModel: ConfirmationModel = new ConfirmationModel();
    public cardPressed: boolean = false;
    public polishPlurals = polishPlurals;
    public editingName: boolean = false;
    public editedName: string = '';
    public renaming: boolean = false;

    get previewSessionIds(): string[] {
        // Show up to 9 sessions for 3x3 preview grid
        return this.folder.sessionIds.slice(0, 9);
    }

    public getSessionThumbnail(sessionId: string): string | null {
        if (!userModule.user) {
            return null;
        }

        const userSessions = sessionsModule.sortedSessions[userModule.user.id] || [];
        const session = userSessions.find((s) => s.id === sessionId);
        
        if (!session) {
            return null;
        }

        const sessionPhotosList = photosModule.sessionPhotos[sessionId];
        const sessionThumbnail = session.thumbnail;
        
        if (
            sessionPhotosList &&
            sessionPhotosList.length > 0 &&
            sessionThumbnail &&
            sessionThumbnail.photoId &&
            sessionThumbnail.photoId.trim() !== '' &&
            sessionPhotosList.some(({ id }) => id === sessionThumbnail.photoId)
        ) {
            return sessionPhotosList
                .find(({ id }) => id === sessionThumbnail.photoId)!
                .smallThumbUrl;
        } else if (sessionThumbnail && sessionThumbnail.url && sessionThumbnail.url.trim() !== '') {
            return sessionThumbnail.url;
        } else if (sessionPhotosList && sessionPhotosList.length > 0) {
            return sessionPhotosList[0].smallThumbUrl;
        }
        
        return null;
    }

    public getImageStyle(sessionId: string): object {
        const thumbnail = this.getSessionThumbnail(sessionId);

        return {
            backgroundImage: `url('${thumbnail || '/img/no_images.png'}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
        };
    }

    public openFolder(event?: MouseEvent) {
        this.$emit('open-folder', this.folder, event);
    }

    public editFolder() {
        this.menu = false;
        this.$emit('edit-folder', this.folder);
    }

    public startNameEdit() {
        this.editedName = this.folder.name;
        this.editingName = true;
        this.$nextTick(() => (this.$refs.nameInput as HTMLInputElement).focus());
    }

    public cancelNameEdit() {
        this.editingName = false;
    }

    public async saveName() {
        if (!this.editingName || this.renaming) return;

        const name = this.editedName.trim();
        if (!name || name === this.folder.name) {
            this.cancelNameEdit();
            return;
        }

        this.renaming = true;
        try {
            await sessionsModule.updateFolderAndSave({
                userId: this.folder.userId,
                folder: { ...this.folder, name },
            });
            (this as any).$vToastify.success({ title: 'Sukces!', body: 'Zmieniono nazwę' });
            this.editingName = false;
        } catch (error) {
            console.error('Failed to rename folder:', error);
            (this as any).$vToastify.error({ title: 'Błąd', body: 'Nie udało się zmienić nazwy' });
            this.$nextTick(() => (this.$refs.nameInput as HTMLInputElement).focus());
        } finally {
            this.renaming = false;
        }
    }

    public async deleteFolder() {
        this.menu = false;

        const confirmed = await this.deleteConfirmationModel.confirm('Czy usunąć ten folder?');

        if (confirmed) {
            this.$emit('delete-folder', this.folder);
        }
    }

    public handleMenuToggle(isOpen: boolean) {
        if (isOpen) {
            // Play menu sound like in SessionListElement
            const menuSound = createInterfaceSound('/sound/slide.wav');
            playInterfaceSound(menuSound).catch(() => {
                // Silent fail for menu sound
            });
        }
    }

    public handleCardMouseDown() {
        this.cardPressed = true;
    }

    public handleCardMouseUp() {
        this.cardPressed = false;
    }
}
